/**
 * Whether the API is reachable. A request that gets no answer, or a 502,
 * 503 or 504, marks it unreachable; the shell then shows its "back soon"
 * state and checks `/api/health/live` with growing pauses (2 s up to 30 s,
 * and at once when the browser comes back online) until it answers.
 *
 * Nothing is probed while requests succeed, so pages that never call the
 * API (the public home page) never load the client or the health contract.
 */
import { useSyncExternalStore } from 'react';

let reachable = true;
let timer: ReturnType<typeof setTimeout> | undefined;
let attempt = 0;
const listeners = new Set<() => void>();

const LIVE = '/api/health/live';
const MAX_PAUSE_MS = 30_000;

function set(next: boolean) {
  if (reachable === next) return;
  reachable = next;
  for (const listener of listeners) listener();
}

/** Asks `/api/health/live` once; true when it answers `ok`. */
export async function probe(fetchFn: typeof fetch = fetch): Promise<boolean> {
  try {
    const response = await fetchFn(LIVE, {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    const type = response.headers.get('content-type') ?? '';
    if (!response.ok || !type.includes('application/json')) return false;
    const [{ apiStateFrom }, body] = await Promise.all([
      import('./health.js'),
      response.json() as Promise<unknown>,
    ]);
    return apiStateFrom(body) === 'online';
  } catch {
    return false;
  }
}

function schedule() {
  if (timer !== undefined) return;
  const pause = Math.min(MAX_PAUSE_MS, 2000 * 2 ** attempt);
  attempt += 1;
  timer = setTimeout(() => {
    timer = undefined;
    void recheck();
  }, pause);
}

/** Checks now; on failure, schedules the next check. */
export async function recheck(): Promise<boolean> {
  const ok = await probe();
  if (ok) {
    attempt = 0;
    set(true);
  } else {
    set(false);
    schedule();
  }
  return ok;
}

/** Records that a request found the API unavailable. */
export function markUnreachable(): void {
  set(false);
  schedule();
}

/** Whether the API answered most recently. */
export function isApiReachable(): boolean {
  return reachable;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the API becomes reachable or unreachable. */
export function useApiReachable(): boolean {
  return useSyncExternalStore(subscribe, isApiReachable, () => true);
}

/**
 * Starts watching: while the API is unreachable, the browser coming back
 * online triggers a check at once.
 *
 * @returns Stops watching.
 */
export function startConnectivityWatch(): () => void {
  const online = () => {
    if (reachable) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    attempt = 0;
    void recheck();
  };
  window.addEventListener('online', online);
  return () => {
    window.removeEventListener('online', online);
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
}
